<template>
  <LayoutTitle>
    <template v-slot:title>Welcome {{ firstName }} {{ lastName }}</template>
    <template v-slot:subtitle>Please create a password.</template>
  </LayoutTitle>

  <ElForm
    ref="form"
    @submit="submitForm"
    v-loading="isLoading"
    class="set-credentials-form"
    novalidate
  >
    <ElFormItem label="Create Password" class="label-on-top password-field">
      <ElInput
        placeholder="Enter password"
        v-model="credentials.password"
        @change="v$.credentials.password.$touch"
        @input="v$.credentials.password.$touch"
        @keyup="
          () => {
            passwordError = false;
          }
        "
        :ref="passwordRefs.PASSWORD"
        :type="inputTypes.PASSWORD"
        :class="{ 'input-error': v$.credentials.password.$error }"
      >
        <template v-slot:suffix>
          <ElButton
            class="icon-password"
            :aria-label="showHidePasswordLabels.SHOW"
            @click="togglePassword(passwordRefs.PASSWORD, $event)"
          ></ElButton>
        </template>
      </ElInput>
      <ValidationErrors
        :errors="v$.credentials.password.$errors"
        singleValidator="passwordServerError"
      />
    </ElFormItem>

    <div
      v-if="v$.credentials.password.$dirty"
      class="password-advanced-walidator"
    >
      <ValidationErrors
        v-if="!v$.credentials.password.maxLength.$response"
        :errors="[v$.credentials.password.maxLength]"
      />

      <p
        class="password-advanced-walidator__text"
        :class="{
          'password-advanced-walidator__text--valid':
            !!credentials.password &&
            v$.credentials.password.minLength.$response &&
            v$.credentials.password.hasLowercaseLetter.$response &&
            v$.credentials.password.hasUppercaseLetter.$response &&
            v$.credentials.password.hasNumber.$response &&
            v$.credentials.password.passwordDoesntContainPartsOfUsername
              .$response,
          'password-advanced-walidator__text--invalid': !(
            !!credentials.password &&
            v$.credentials.password.minLength.$response &&
            v$.credentials.password.hasLowercaseLetter.$response &&
            v$.credentials.password.hasUppercaseLetter.$response &&
            v$.credentials.password.hasNumber.$response &&
            v$.credentials.password.passwordDoesntContainPartsOfUsername
              .$response
          ),
        }"
      >
        Your password needs to include:
      </p>
      <PasswordRuleMet
        msg="At least 8 characters"
        :met="
          !!credentials.password && v$.credentials.password.minLength.$response
        "
      />
      <PasswordRuleMet
        msg="A lowercase letter"
        :met="
          !!credentials.password &&
          v$.credentials.password.hasLowercaseLetter.$response
        "
      />
      <PasswordRuleMet
        msg="An uppercase letter"
        :met="
          !!credentials.password &&
          v$.credentials.password.hasUppercaseLetter.$response
        "
      />
      <PasswordRuleMet
        msg="A number"
        :met="
          !!credentials.password && v$.credentials.password.hasNumber.$response
        "
      />
      <PasswordRuleMet
        msg="No parts of username"
        :met="
          !!credentials.password &&
          v$.credentials.password.passwordDoesntContainPartsOfUsername.$response
        "
      />
    </div>

    <ElFormItem class="password-field">
      <ElInput
        placeholder="Repeat password"
        v-model="credentials.repeatedPassword"
        @change="v$.credentials.repeatedPassword.$touch"
        @input="v$.credentials.repeatedPassword.$touch"
        :ref="passwordRefs.REPEATED"
        :type="inputTypes.PASSWORD"
        :class="{ 'input-error': v$.credentials.repeatedPassword.$error }"
      >
        <template v-slot:suffix>
          <ElButton
            class="icon-password"
            :aria-label="showHidePasswordLabels.SHOW"
            @click="togglePassword(passwordRefs.REPEATED, $event)"
          ></ElButton>
        </template>
      </ElInput>
      <ValidationErrors :errors="v$.credentials.repeatedPassword.$errors" />
    </ElFormItem>
    <ElFormItem class="label-on-top" label="Choose a security question">
      <ElSelect
        v-model="credentials.recoveryQuestion"
        placeholder="Select question"
        class="full-width"
      >
        <ElOption
          v-for="q in recoveryQuestions"
          :key="q"
          :label="q.questionText"
          :value="q.questionText"
        >
        </ElOption>
      </ElSelect>
    </ElFormItem>
    <ElFormItem class="no-bottom-margin">
      <ElInput
        placeholder="Enter the answer"
        v-model="credentials.answer"
        @change="v$.credentials.answer.$touch"
        @keyup="
          () => {
            answerError = false;
          }
        "
        type="text"
        :class="{ 'input-error': v$.credentials.answer.$error }"
      ></ElInput>
      <ValidationErrors
        :single="true"
        :errors="v$.credentials.answer.$errors"
      />
    </ElFormItem>
    <ElFormItem class="no-top-margin">
      <ElButton
        native-type="submit"
        class="submit-btn"
        :disabled="v$.credentials.$error"
        >Save</ElButton
      >
    </ElFormItem>
  </ElForm>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import {
  ElButton,
  ElCard,
  ElCheckbox,
  ElCheckboxGroup,
  ElForm,
  ElSteps,
  ElInput,
} from "element-plus";
import useVuelidate, { Validation, ValidatorFn } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import {
  n9maxLengthWithMsg,
  n9minLengthWithMsg,
  n9Required,
  notPartOf,
  wordsNotInContext,
} from "@/services/validators/common-validators";
import SideWidget from "@/modules/ui/SideWidget.vue";
import LayoutTitle from "@/modules/ui/LayoutTitle.vue";
import { configService } from "@/services/config-service";
import ValidationErrors from "@/modules/ui/ValidationErrors.vue";
import PasswordRuleMet from "@/modules/ui/PasswordRuleMet.vue";
import {
  FinalizeReponse,
  onboardingService,
  RecoveryQuestion,
} from "@/services/onboarding/onboarding-service";
import { RoutesName } from "@/router";
import { SetCredentialsPayload } from "@/services/onboarding/model/setCredentials";
import {
  HttpBadRequestError,
  ValidationErrorMessage,
  ValidationErrorMessageCode,
} from "@/services/http/httpErrors";
import store from "@/store";

const hasLowercaseLetter = helpers.regex(/.*[a-z].*/);
const hasUppercaseLetter = helpers.regex(/.*[A-Z].*/);
const hasNumber = helpers.regex(/.*[0-9].*/);

enum InputTypes {
  TEXT = "text",
  PASSWORD = "password",
}

enum ShowHidePasswordLabels {
  SHOW = "Show password",
  HIDE = "Hide password",
}

enum PasswordRefs {
  PASSWORD = "password",
  REPEATED = "repeatedPassword",
}

const repeatedPasswordIsTheSame = (value: string, ctx: Credentials) => {
  return !helpers.req(value) || ctx.password === value;
};

const notPartOfUsername = (
  value: string,
  ctx: Credentials,
  vm: SetCredentials
) => {
  return notPartOf(value, vm.email);
};

const notPartOfQuestion = (value: string, ctx: Credentials) => {
  return wordsNotInContext(value, ctx.recoveryQuestion);
};

const notPartOfUsernameOrPassword = (
  value: string,
  ctx: Credentials,
  vm: SetCredentials
): boolean => {
  return !(
    ctx.password.length &&
    (value.toLowerCase().includes(vm.username.toLowerCase()) ||
      value.toLowerCase().includes(ctx.password.toLowerCase()) ||
      vm.username.toLowerCase().includes(value.toLowerCase()) ||
      ctx.password.toLowerCase().includes(value.toLowerCase()))
  );
};

const answerServerError = (
  value: string,
  ctx: Credentials,
  vm: SetCredentials
): boolean => {
  return !vm.answerError;
};

const passwordServerError = (
  value: string,
  ctx: Credentials,
  vm: SetCredentials
): boolean => {
  return !vm.passwordError;
};

export class Credentials {
  password = "";
  repeatedPassword = "";
  recoveryQuestion = "";
  answer = "";
}

@Options({
  components: {
    ValidationErrors,
    ElCard,
    ElSteps,
    ElForm,
    ElButton,
    ElCheckboxGroup,
    ElCheckbox,
    LayoutTitle,
    SideWidget,
    PasswordRuleMet,
  },
  validations: {
    credentials: {
      password: {
        required: n9Required("Password"),
        minLength: n9minLengthWithMsg(8),
        maxLength: n9maxLengthWithMsg(),
        hasLowercaseLetter,
        hasUppercaseLetter,
        hasNumber,
        passwordDoesntContainPartsOfUsername: helpers.withMessage(
          "Passwords contains parts of username!",
          notPartOfUsername as ValidatorFn
        ),
        passwordServerError: helpers.withMessage(
          `Your password is not safe. It is too similar to your login.`,
          passwordServerError as ValidatorFn
        ),
      },
      repeatedPassword: {
        required,
        maxLength: n9maxLengthWithMsg(),
        repeatedPasswordIsTheSame: helpers.withMessage(
          "Passwords do not match!",
          repeatedPasswordIsTheSame as ValidatorFn
        ),
      },
      answer: {
        required: n9Required("Answer"),
        maxLength: n9maxLengthWithMsg(),
        minLength: n9minLengthWithMsg(4),
        notPartOfQuestion: helpers.withMessage(
          `Answer cannot contain parts of the security question`,
          notPartOfQuestion as ValidatorFn
        ),
        notPartOfUsernameOrPassword: helpers.withMessage(
          `Answer cannot be user name or user's password`,
          notPartOfUsernameOrPassword as ValidatorFn
        ),
        answerServerError: helpers.withMessage(
          `Your answer is not safe. It is too similar to your login, password or security question.`,
          answerServerError as ValidatorFn
        ),
      },
      recoveryQuestion: {
        required: n9Required("Recovery question"),
        maxLength: n9maxLengthWithMsg(),
      },
    },
  },
})
export default class SetCredentials extends Vue {
  $store = store;
  credentials = new Credentials();

  v$ = useVuelidate() as unknown as Validation;

  config = configService;
  isLoading = false;
  passwordError = false;
  answerError = false;

  inputTypes = InputTypes;
  showHidePasswordLabels = ShowHidePasswordLabels;
  passwordRefs = PasswordRefs;

  get email(): string {
    return this.$store.state.prospect.email;
  }

  get username(): string {
    return this.email.split("@")[0] || "";
  }

  get firstName(): string {
    return this.$store.state.prospect.firstName;
  }

  get lastName(): string {
    return this.$store.state.prospect.lastName;
  }

  get recoveryQuestions(): RecoveryQuestion[] {
    return this.$store.state.recoveryQuestions;
  }

  mounted(): void {
    this.setFirstRecoveryQuestionAsDefault();
  }

  async submitForm(e: Event): Promise<void> {
    e.preventDefault();
    this.v$.$touch();

    if (this.v$.$error) return;

    this.isLoading = true;

    const credentialsPayload = new SetCredentialsPayload(
      this.credentials.password,
      this.credentials.recoveryQuestion,
      this.credentials.answer
    ).getPayload();

    try {
      const jwt = this.$store.getters.jwt;
      const credentialsSuccessResponse =
        await onboardingService.submitCredentials(credentialsPayload, jwt);
      if (credentialsSuccessResponse) {
        this.autologin(credentialsSuccessResponse);
      }
    } catch (e) {
      this.isLoading = false;
      if (e instanceof HttpBadRequestError) {
        switch (e.status) {
          case 422:
            const errorMessage = await e.errorMessage;
            this.setErrorMessage(errorMessage);
            break;
          case 404:
            this.$router.push({ name: RoutesName.STAGED_LICENSE_NOT_FOUND });
            break;
          case 410:
            this.$router.push({ name: RoutesName.STAGED_TOKEN_EXPIRED_ERROR });
            break;
          default:
            this.$router.push({ name: RoutesName.SET_CREDENTIALS_ERROR });
        }
      } else {
        this.$router.push({ name: RoutesName.SET_CREDENTIALS_ERROR });
      }
    }
  }

  autologin(setCredentalsSuccessResponse: FinalizeReponse): void {
    let location = window.location.origin;
    if (
      location === "https://localhost" ||
      location.startsWith("https://localhost:")
    ) {
      location = location.replace("https://", "http://");
    }

    window.location.href = `${location}/init-session/${setCredentalsSuccessResponse.sessionToken}`;
  }

  togglePassword(refName: PasswordRefs, { target }: Event): void {
    const inputRef = this.$refs[refName] as typeof ElInput;

    this.toggleType(inputRef.input, this.switchType);
    inputRef.focus();

    this.toggleClassname(target as HTMLButtonElement);
    this.toggleAriaLabel(target as HTMLButtonElement, this.switchAria);
  }

  private setFirstRecoveryQuestionAsDefault(): void {
    this.credentials.recoveryQuestion = this.recoveryQuestions[0].questionText;
  }

  private setErrorMessage(errorMessage: ValidationErrorMessage): void {
    switch (errorMessage.error.code) {
      case ValidationErrorMessageCode.INVALID_SECURITY_QUESTION_ANSWER:
        this.answerError = true;
        break;
      case ValidationErrorMessageCode.INVALID_PASSWORD:
        this.passwordError = true;
        break;
      default:
        this.passwordError = false;
        this.answerError = false;
    }
  }

  private toggleType(
    input: HTMLInputElement,
    toggler: (_: string) => string
  ): void {
    input.type = toggler(input.type);
  }

  private toggleClassname(element: HTMLButtonElement): void {
    element.classList.toggle("icon-password--hide");
  }

  private toggleAriaLabel(
    element: HTMLButtonElement,
    toggler: (_: string | null) => string
  ): void {
    element.ariaLabel = toggler(element.ariaLabel);
  }

  private switchType(current: string): string {
    const { TEXT, PASSWORD } = InputTypes;

    return current === PASSWORD ? TEXT : PASSWORD;
  }

  private switchAria(current: string | null): string {
    const { SHOW, HIDE } = ShowHidePasswordLabels;

    return current === SHOW ? HIDE : SHOW;
  }
}
</script>

<style lang="scss">
.set-credentials-form {
  .password-field {
    margin-bottom: 4px;

    .icon-password {
      width: 25px;
      background-image: url("../../assets/img/icon-show-password.svg");
      background-repeat: no-repeat;
      background-position: center;
      background-color: transparent;
    }

    .icon-password--hide {
      background-image: url("../../assets/img/icon-hide-password.svg");
    }
  }

  .label-on-top {
    display: flex;
    flex-direction: column;

    label {
      display: flex;
      line-height: 36px;
      justify-content: flex-start;
    }
  }

  .submit-btn {
    margin-top: 46px;
    margin-bottom: 16px;
  }

  .full-width {
    width: 100%;
  }

  .password-advanced-walidator {
    padding-left: 10px;
    margin-top: 8px;
    margin-bottom: 16px;

    &__text {
      font-size: 12px;
      margin-top: 5px;
      margin-bottom: 5px;

      &--valid {
        color: var(--success-color);
      }

      &--invalid {
        color: var(--error-color);
      }
    }
  }

  .el-form-item {
    margin-top: 16px;
  }

  .el-input__suffix {
    margin-right: 12px;
  }

  .no-bottom-margin {
    margin-bottom: 0;
  }

  .no-top-margin {
    margin-top: 0;
  }
}
</style>
